<template>
  <div class="input-autocomplete">
      <b-form-group :class="[
          `fieldset-${objectKeyName}`,
          'autocomplete-input-group'
        ]" role="search">
        <b-row no-gutters>
          <b-col cols="12">
            <div class="fieldset--label">
              <label @mousedown="isLabelClicked = true" :for=inputId class="d-inline-block" v-if="label">{{ label }}</label>
              <button v-if="labelIconWithModal" @click="showLabelModal=true" class="icon-only" type="button" aria-haspopup="dialog" :aria-label="getSlotText('txtLabelIcon')">
                <img src="/files/flash/images/svg-icons/info-icon.svg" width="16" height="16" alt="">
              </button>
              <SimonModal v-if="labelIconWithModal" :is-visible="showLabelModal" @hide="showLabelModal = false"
              :aria-label-modal="getSlotText('ariaLabelModal')"
              >
                <slot name="txtModal"></slot>
              </SimonModal>
            </div>
            <div class="dropdown-wrapper" @scroll="grabScrollEvent">
              <div class="dropdown" :ref="'dropdown' + $vnode.key">
                <div :id="$vnode.key" class="dropdown-border">
                  <input
                    :id=inputId
                    ref="autocompleteInputField"
                    :aria-controls="inputId + '-listbox'"
                    :aria-activedescendant="focusedItem !== null ? 'item-' + focusedItem : null"
                    class="form-control"
                    :class="[!isOptionListVisible || showAlwaysDefaultIcon || selectedItem === '' || selectedItem === null ? defaultIcon + (isOptionListVisible && !showAlwaysDefaultIcon ? focusIcon : '') : '', isOptionListVisible ? 'option-list-active' : '', isValidItemSelected && showSuccessIcon ? 'valid' : '', (selectedItem !== '' && selectedItem !== null && isOptionListVisible && !this.showAlwaysDefaultIcon ? 'clearable' : ''), pointer ? 'pointer' : '', isFocusedWithoutClick ? 'is-focus-visible' : '']"
                    type="text"
                    name="autocompleteInputField"
                    spellcheck="false"
                    v-bind:value="selectedItem"
                    :placeholder="placeholder"
                    :role="inputReadOnly ? 'listbox' : 'combobox'"
                    aria-haspopup="listbox"
                    :aria-autocomplete="inputReadOnly ? 'none' : 'both'"
                    :aria-live="ariaLiveForInput"
                    :aria-label="ariaLabelForInput"
                    :aria-describedby="ariaDescribedBy"
                    :aria-expanded="searchingItem ? 'true' : 'false'"
                    @keyup.enter.prevent="onSelectByReturnKey()"
                    @keydown="onArrowKeyScroll"
                    @keyup.tab="onEnterByTab()"
                    @keydown.esc.prevent="onEscape"
                    @keydown.tab="onBlurByTab()"
                    @focus="onFocusSelectField()"
                    @input="reactOnUserInput($event.target.value); $emit('input');"
                    @blur="isNavigatingInProviders = false; isUserTyping = false; isClicked = false; isFocusedWithoutClick = false"
                    @mousedown="isClicked = true"
                    autocomplete="off"
                    :disabled="disabled"
                    :readonly="inputReadOnly"
                  >
                </div>
                <button ref="clearInputButton" aria-label="Eingabe entfernen" type="button" :id=clearInputId class="clear-input icon-only" v-if="isOptionListVisible && selectedItem !== '' && selectedItem !== null && !this.showAlwaysDefaultIcon" @click.prevent="isClicked = true; clearSelection()" @keydown.enter.prevent="isClearedByKey = true; clearSelection()"  @focus="isOptionListVisible = true; focusedItem = -1" @focusout="isOptionListVisible = false"></button>
                <div class="option-list-wrapper" @mousemove="scrollHandlerOnDrag" @drag="scrollHandlerOnDrag" @mouseover="scrollHandlerOnDragCatch" @mouseup="scrollHandlerOnDragEnd" @mouseleave="scrollHandlerOnDragEnd">
                  <!-- Screenreader live region for no results -->
                  <div aria-live="polite" aria-atomic="true" class="sr-only" ref="liveRegion"></div>
                  <ul ref="dropdownListContainer" :id="inputId + '-listbox'" role="listbox" tabindex="-1"
                      :aria-label="listAriaLabel"
                      v-if="isOptionListVisible" class="option-list" @scroll="scrollHandlerOnScroll">
                    <li aria-disabled="true" aria-hidden="true" class="no-item-found" role="option"
                        v-if="(!filteredSortedData || filteredSortedData.length === 0) && notFoundInputMsg">
                      {{ notFoundInputMsg }}
                    </li>
                    <li v-for="(item, index) in filteredSortedData"
                        :id="'item-' + index"
                        :class="[{ focus: index === focusedItem }, item.frequency, { last: getLast(item, objectKeyName, lastListItem) }]"
                        :key="item[objectKeyName]"
                        role="option"
                        :aria-selected="index === focusedItem"
                        @mouseover.prevent="listItemMouseOver(index)"
                        @mouseleave.prevent="listItemMouseLeave(index)"
                        @click="itemClicked(item[objectKeyName])"
                    >
                      <div class="item-wrapper">
                        <div class="name">{{ item[objectKeyName] }}</div>
                      </div>
                    </li>
                  </ul>
                  <div ref="scrollHandle" class="scroll-handle" v-if="isOptionListVisible" @mousedown="scrollHandlerOnDragStart"></div>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
        <div class="info-message" v-if="isValidItemSelected && successMsg && selectedItem">{{successMsg}}</div>
        <div class="info-message" v-if="failureMsg && selectedItem">{{failureMsg}}</div>
      </b-form-group>
  </div>
</template>

<script>

import SimonModal from '@/components/modal/SimonModal.vue'
import preFilledInput from '@/mixins/preFilledInput'

export default {
  name: 'InputAutocomplete',
  components: {
    SimonModal
  },
  mixins: [
    preFilledInput('selectedItem', 'storedItem')
  ],
  props: {
    storedItem: {
      type: String,
      default: ''
    },
    inputId: {
      type: String,
      default: 'autocompleteField'
    },
    ariaDescribedBy: {
      type: String,
      default: 'autocompleteField_info-box'
    },
    clearInputId: {
      type: String,
      default: 'clear-input'
    },
    // Array of objects
    data: {
      type: Array,
      default: () => [],
      required: true
    },
    // The data prop receives an array of objects. Define here the key name of the object to filter and show.
    objectKeyName: {
      type: String,
      required: true,
      default: 'name'
    },
    label: {
      type: String,
      default: null
    },
    labelAriaLabel: {
      type: String,
      default: null
    },
    inputAriaLabel: {
      type: String,
      default: null
    },
    listAriaLabel: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Suche...'
    },
    // Message to be shown inside input field if not term found
    notFoundInputMsg: {
      type: String
    },
    // Message to be shown under the input field
    successMsg: {
      type: String,
      default: null
    },
    // Message to be shown under the input field
    failureMsg: {
      type: String,
      default: null
    },
    showSuccessIcon: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    sortData: {
      type: Boolean,
      default: true
    },
    last: {
      type: String,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    labelIconWithModal: {
      type: Boolean,
      default: false
    },
    defaultIcon: {
      type: String,
      default: 'search'
    },
    hardFilter: {
      type: Boolean,
      default: true
    },
    withPreSave: {
      type: Boolean,
      default: false
    },
    pointer: {
      type: Boolean,
      default: false
    },
    showAlwaysDefaultIcon: {
      type: Boolean,
      default: false
    },
    withFocus: {
      type: Boolean,
      default: true
    },
    focusIcon: {
      type: String,
      default: ' focus'
    },
    focusNext: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      focusedItem: 0,
      showLabelModal: false,
      isOptionListVisible: false,
      scrollHandlerDragging: false,
      scrollHandlerDragStartPos: 0,
      scrollHandlerDragLastPageY: 0,
      elementAutocompleteField: null,
      preSaveItem: '',
      searchingItem: false,
      isNavigatingInProviders: false,
      isUserTyping: false,
      isClearedByKey: false,
      isLabelClicked: false,
      isFocusedWithoutClick: false,
      isClicked: false,
      liveToggleChar: true
    }
  },
  mounted () {
    document.addEventListener('click', this.documentClicked)
    document.addEventListener('focusout', this.focusChanged)
    this.elementAutocompleteField = document.getElementById('autocompleteField')
  },
  computed: {
    inputReadOnly: function () {
      return this.readOnly
    },
    sortedData: function () {
      const loadedData = this.data // create copy to sort prefetched list
      return this.sortData ? loadedData.sort((a, b) => {
        const na = a[this.objectKeyName].toLowerCase()
        const nb = b[this.objectKeyName].toLowerCase()
        return na.localeCompare(nb)
      }) : this.data
    },
    filteredSortedData () {
      return this.hardFilter ? this.sortedData.filter((item) => { return item[this.objectKeyName] })
        .filter((item) => { return (`${item[this.objectKeyName]}`.toLowerCase().indexOf(this.selectedItem.toLowerCase()) >= 0) }) : this.sortedData
    },
    isValidItemSelected () {
      let valid = false
      if (this.selectedItem && this.filteredSortedData.length) {
        valid = this.filteredSortedData.filter((item) => { return (item[this.objectKeyName].toLowerCase() === this.selectedItem.toLowerCase()) }).length === 1
      }
      return valid && !this.isOptionListVisible
    },
    lastListItem () {
      return this.filteredSortedData[this.filteredSortedData.length - 1][this.objectKeyName]
    },
    ariaLiveForInput () {
      if (this.isNavigatingInProviders) {
        return 'polite'
      } else if (this.isUserTyping) {
        return 'polite'
      } else if (this.inputReadOnly) {
        return 'polite'
      } else { return 'off' }
    },
    ariaLabelForInput () {
      if (this.isNavigatingInProviders || (this.isUserTyping && this.filteredSortedData.length === 1)) {
        return ''
      } else if (this.isUserTyping) {
        return ''
      } else if (this.selectedItem) {
        return this.selectedItem + ' selektiert.'
      } else { return this.inputAriaLabel }
    }
  },
  methods: {
    announceToScreenReader (message) {
      const region = this.$refs.liveRegion
      if (!region) return
      // They are visually identical, but different in the DOM, which tricks Screenreader into reading the message again.
      const toggleChar = this.liveToggleChar ? '\u200B' : '\u200C'
      region.textContent = message + toggleChar

      this.liveToggleChar = !this.liveToggleChar
    },
    getLast (item, objectKeyName, lastListItem) {
      return item[objectKeyName] === lastListItem || item[objectKeyName] === this.last
    },
    getInput () {
      return this.$refs.autocompleteInputField
    },
    grabScrollEvent (e) {
      if (e.stopPropagation) {
        e.stopPropagation()
      }
    },
    reactOnUserInput (value) {
      this.isNavigatingInProviders = false
      if (document.activeElement === this.$refs.autocompleteInputField) {
        this.isUserTyping = true
      }
      this.selectedItem = value
      if (this.focusedItem > this.filteredSortedData.length) {
        this.focusedItem = 0
      }
      if (this.selectedItem !== '') {
        this.isOptionListVisible = true
        this.focusedItem = -1
        if (this.selectedItem.length > 2) {
          // at 3. letter for performance reasons while typing
          const match = this.filteredSortedData.filter((item) => { return (item[this.objectKeyName].toLowerCase() === this.selectedItem.toLowerCase()) })
          if (match.length === 1) {
            this.selectedItem = match[0][this.objectKeyName]
          }
          this.emitSelectedItemChange()
        }
      }
    },
    itemSelection () {
      this.isOptionListVisible = false
      this.isNavigatingInProviders = false
      this.isUserTyping = false
      // this.$refs.autocompleteInputField.blur()
      this.emitSelectedItemChange()
    },
    resetAutocompleteInputField () {
      this.selectedItem = ''
      this.itemSelection()
    },
    emitSelectedItemChange () {
      this.$emit('selectedItemChange', this.selectedItem)
    },
    clearSelection () {
      this.preSaveItem = this.selectedItem
      if (this.isOptionListVisible && !this.showAlwaysDefaultIcon) {
        this.resetAutocompleteInputField()
      }
      if (this.focusNext !== '') {
        this.$nextTick(() => {
          const element = document.querySelector(this.focusNext)
          element.focus()
        })
      }

      this.$nextTick(() => {
        if (this.withFocus) {
          this.isOptionListVisible = false
          this.$refs.autocompleteInputField.focus()
        }
      })
    },
    calculateScrollRatio () {
      const sh = this.$refs.dropdownListContainer.scrollHeight
      const ch = this.$refs.dropdownListContainer.clientHeight
      return (ch / sh)
    },
    scrollHandlerOnScroll (e) {
      if (!this.scrollHandlerDragging && this.$refs.dropdownListContainer) {
        const ch = this.$refs.dropdownListContainer.clientHeight
        const st = this.$refs.dropdownListContainer.scrollTop
        const sr = this.calculateScrollRatio()
        const raf = window.requestAnimationFrame || window.setImmediate || function (c) { return setTimeout(c, 0) }

        // scrollable
        if (sr < 1) {
          // calculate top position of scroll handler within list
          let ht = Math.floor(st * sr)
          if (ht < 10) {
            ht = 10
          }
          if (ht > ch - 30) {
            ht = ch - 30
          }

          raf(() => {
            if (this.$refs.scrollHandle) {
              this.$refs.scrollHandle.style.display = 'block'
              this.$refs.scrollHandle.style.top = ht + 'px'
            }
          })
        } else {
          raf(() => {
            // nothing to scroll
            if (this.$refs.scrollHandle) {
              this.$refs.scrollHandle.style.display = 'none'
            }
          })
        }
      }
    },
    onFocusSelectField () {
      this.isOptionListVisible = true
      this.focusedItem = -1

      if (!this.isClicked && !this.isLabelClicked) {
        this.isFocusedWithoutClick = true
      }

      this.isLabelClicked = false
      this.$nextTick(() => {
        this.scrollHandlerOnScroll()
      })
    },
    scrollHandlerOnDragCatch (e) {
      if (this.scrollHandlerDragging) {
        e.preventDefault()
      }
    },
    scrollHandlerOnDragEnd (e) {
      this.scrollHandlerDragging = false
    },
    scrollHandlerOnDrag (e) {
      e.preventDefault()
      if (this.scrollHandlerDragging) {
        const sr = this.calculateScrollRatio()
        const ch = this.$refs.dropdownListContainer.clientHeight
        const delta = e.pageY - this.scrollHandlerDragLastPageY
        this.scrollHandlerDragLastPageY = e.pageY
        let cp = (this.scrollHandlerDragStartPos += delta)

        // boundaries
        if (cp < 10) {
          cp = 10
        }
        if (cp > ch - 30) {
          cp = ch - 30
        }

        const raf = window.requestAnimationFrame || window.setImmediate || function (c) { return setTimeout(c, 0) }

        raf(() => {
          this.$refs.dropdownListContainer.scrollTop += delta / sr
          this.$refs.scrollHandle.style.top = cp + 'px'
        })
      }
    },
    listItemMouseOver (index) {
      if (!this.scrollHandlerDragging) {
        this.focusedItem = index
      }
    },
    listItemMouseLeave (index) {
      if (!this.scrollHandlerDragging) {
        this.focusedItem = index
      }
    },
    scrollHandlerOnDragStart (e) {
      e.preventDefault()
      this.scrollHandlerDragging = true
      this.scrollHandlerDragStartPos = this.$refs.scrollHandle.offsetTop
      this.scrollHandlerDragLastPageY = e.pageY
    },
    onArrowKeyScroll (event) {
      const elFocus = document.querySelector('.autocomplete-input-group .focus')
      const elOptionList = document.querySelector('.autocomplete-input-group .option-list')
      const numEl = this.filteredSortedData.length
      let elHeight
      let isNavigationKeyPressed = false
      let scrollTo = 0

      if (elFocus) {
        elHeight = elFocus.offsetHeight
      } else {
        elHeight = 30
      }
      switch (event.keyCode) {
        // page up
        case 33:
          this.focusedItem = 0
          scrollTo = 0
          isNavigationKeyPressed = true
          break
        // arrow up
        case 38:
          this.isNavigatingInProviders = true
          if (this.focusedItem > 0) {
            this.focusedItem--
          }
          scrollTo = (this.focusedItem * elHeight)
          isNavigationKeyPressed = true
          break
        // page down
        case 34:
          this.focusedItem = numEl - 1
          scrollTo = (this.focusedItem * elHeight)
          isNavigationKeyPressed = true
          break
        // arrow down
        case 40:
          this.isNavigatingInProviders = true
          if (this.focusedItem < numEl - 1) {
            this.focusedItem++
          }
          scrollTo = (this.focusedItem * elHeight)
          isNavigationKeyPressed = true
          break
      }
      // ensure valid range
      if (scrollTo < 0) {
        scrollTo = 0
      }
      if (scrollTo > (numEl - 1) * elHeight) {
        scrollTo = (numEl - 1) * elHeight
      }
      if (elOptionList) {
        elOptionList.scrollTop = scrollTo
      }
      if (isNavigationKeyPressed) {
        event.preventDefault()
      }
    },
    onSelectByReturnKey () {
      if (this.isClearedByKey) {
        this.isClearedByKey = false
        return
      }
      if (this.filteredSortedData[this.focusedItem]) {
        this.selectedItem = this.filteredSortedData[this.focusedItem][this.objectKeyName]
      }
      this.itemSelection()

      if (this.focusNext !== '') {
        this.$nextTick(() => {
          const element = document.querySelector(this.focusNext)
          element.focus()
        })
      }
    },
    onEnterByTab () {
      this.onFocusSelectField()
    },
    onBlurByTab () {
      requestAnimationFrame(() => {
        if (document.activeElement !== this.$refs.clearInputButton) {
          this.isOptionListVisible = false
        }
      })
    },
    onEscape (event) {
      if (this.isOptionListVisible) {
        this.isOptionListVisible = false
        event.stopPropagation()
      }
    },
    itemClicked (itemName) {
      this.selectedItem = itemName
      this.preSaveItem = itemName
      this.itemSelection()
    },
    showItemInformation (item) {
      this.isOptionListVisible = false
      this.focusedItem = 0
    },
    documentClicked (event) {
      if (this.isOptionListVisible) {
        const el = this.$refs['dropdown' + this.$vnode.key]
        if (el && el !== event.target && !el.contains(event.target) && event.target.id !== this.clearInputId) {
          this.showItemInformation(this.selectedItem)
          if (this.withPreSave) {
            this.selectedItem = this.preSaveItem
          }
          this.emitSelectedItemChange()
        }
      }
    },
    focusChanged (event) {
      if (event) {
        const el = this.$refs['dropdown' + this.$vnode.key]
        if (el !== undefined && el !== event.target && !el.contains(event.target)) {
        } else {
          if ((navigator.userAgent.indexOf('MSIE') !== -1) || (!!document.documentMode === true)) {
            if (this.elementAutocompleteField) {
              this.elementAutocompleteField.tabIndex = 0
            }
          } else {
            this.$nextTick(function () {
              if (this.elementAutocompleteField) {
                this.elementAutocompleteField.tabIndex = 0
              }
            })
          }
        }
      }
    }
  },
  watch: {
    filteredSortedData (newVal) {
      if ((!newVal || newVal.length === 0) && this.notFoundInputMsg) {
        this.announceToScreenReader(this.notFoundInputMsg)
      }
    }
  }
}
</script>
